import { useEffect, useMemo, useState } from "react";
import { Swords, Users, Hash, Link2, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "@/lib/audit";
import {
  challengeStats,
  createChallenge,
  listChallenges,
} from "@/lib/viralSprintV3/challengeFriend";
import {
  createSocialQuiniela,
  listQuinielas,
  bumpQuinielaJoin,
} from "@/lib/viralSprintV3/socialQuiniela";
import { formatHashtagBlock } from "@/lib/viralSprintV3/dynamicHashtag";
import { withAutoReferral } from "@/lib/viralSprintV3/autoReferral";
import {
  getInviterToken,
  markReciprocalPrompted,
  shouldPromptReciprocal,
} from "@/lib/viralSprintV3/reciprocalInvite";
import { useSelectedNation } from "@/hooks/useSelectedNation";

interface Props {
  matchId?: string | null;
  homeCode?: string | null;
  awayCode?: string | null;
  stage?: string | null;
}

function shareLink(url: string, message: string) {
  if (typeof window === "undefined") return;
  if (typeof navigator !== "undefined" && navigator.share) {
    navigator.share({ title: "VerseSport", text: message, url }).catch(() => {
      window.open(`https://wa.me/?text=${encodeURIComponent(`${message} ${url}`)}`, "_blank", "noopener,noreferrer");
    });
    return;
  }
  window.open(`https://wa.me/?text=${encodeURIComponent(`${message} ${url}`)}`, "_blank", "noopener,noreferrer");
}

export function ViralSprintV3Hub({ matchId = null, homeCode = null, awayCode = null, stage = null }: Props) {
  const { nation } = useSelectedNation();
  const [tick, setTick] = useState(0);
  const [prediction, setPrediction] = useState("MEX 2-1 ARG");
  const [quinielaName, setQuinielaName] = useState("Mi crew SS2026");

  const hashtagBlock = useMemo(
    () => formatHashtagBlock({ matchId, homeCode, awayCode, stage }),
    [matchId, homeCode, awayCode, stage],
  );

  const baseUrl = "https://versesport.com";
  const chStats = challengeStats();
  const challenges = listChallenges();
  const quinielas = listQuinielas();
  const reciprocalDue = shouldPromptReciprocal();
  const inviter = getInviterToken();

  useEffect(() => {
    void trackEvent("viral_sprint_hub_viewed", { matchId, nation });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSendChallenge = () => {
    const rec = createChallenge({ matchId, prediction });
    const url = withAutoReferral(`${baseUrl}/?challenge=${rec.id}`, {
      source: "challenge", medium: "whatsapp", campaign: "v3_viral",
    });
    const msg = `🔥 Te reto: ${rec.prediction} en ${homeCode ?? "?"} vs ${awayCode ?? "?"}. ¿Te animas? ${hashtagBlock}`;
    shareLink(url, msg);
    void trackEvent("viral_challenge_sent", { id: rec.id, matchId });
    setTick((x) => x + 1);
  };

  const onCreateQuiniela = () => {
    const q = createSocialQuiniela({ name: quinielaName, matchId });
    const url = withAutoReferral(`${baseUrl}/?quiniela=${q.id}`, {
      source: "quiniela_social", medium: "whatsapp", campaign: "v3_viral",
    });
    const msg = `🎯 Únete a mi quiniela "${q.name}" para ${homeCode ?? "?"} vs ${awayCode ?? "?"}. ${hashtagBlock}`;
    shareLink(url, msg);
    void trackEvent("viral_quiniela_created", { id: q.id, matchId });
    bumpQuinielaJoin(q.id);
    setTick((x) => x + 1);
  };

  const onCopyHashtags = async () => {
    try {
      await navigator.clipboard.writeText(hashtagBlock);
      toast.success("Hashtags copiados");
      void trackEvent("viral_hashtag_copied", { hashtagBlock });
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const onReciprocalInvite = () => {
    const url = withAutoReferral(baseUrl, {
      source: "reciprocal", medium: "whatsapp", campaign: "v3_viral",
    });
    const msg = `Un amigo me invitó a VerseSport y está increíble. Te paso el link 👇`;
    shareLink(url, msg);
    markReciprocalPrompted();
    void trackEvent("viral_reciprocal_invite_sent", { inviter });
    setTick((x) => x + 1);
  };

  return (
    <div className="space-y-3 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-background/40 to-background/40 p-3">
      <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-primary">
        <Swords className="h-3.5 w-3.5" /> Sprint Viral V3
      </div>

      {/* Reciprocal invite prompt */}
      {reciprocalDue && (
        <div className="rounded-xl border border-[oklch(0.78_0.2_60)]/40 bg-[oklch(0.78_0.2_60)]/10 p-3">
          <div className="text-[11px] font-bold">Te invitaron 🤝 invita tú también</div>
          <div className="text-[10px] text-muted-foreground">Devuelve la energía y suma puntos extra.</div>
          <button
            onClick={onReciprocalInvite}
            className="mt-2 flex w-full items-center justify-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-[11px] font-bold text-primary-foreground"
          >
            <RefreshCcw className="h-3 w-3" /> Invitar de vuelta
          </button>
        </div>
      )}

      {/* Challenge a friend */}
      <div className="rounded-xl border border-border/60 bg-background/60 p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-[11px] font-bold"><Swords className="h-3 w-3 text-primary" /> ¿Quién sabe más de fútbol?</div>
          <div className="text-[9px] text-muted-foreground">{chStats.accepted}/{chStats.sent} aceptados</div>
        </div>
        <input
          value={prediction}
          onChange={(e) => setPrediction(e.target.value)}
          maxLength={40}
          className="mt-2 w-full rounded-md border border-border/60 bg-background/80 px-2 py-1 text-[11px]"
          placeholder="Tu predicción"
        />
        <button
          onClick={onSendChallenge}
          className="mt-2 w-full rounded-lg bg-primary px-3 py-1.5 text-[11px] font-bold text-primary-foreground"
        >
          Enviar reto
        </button>
        {challenges.length > 0 && (
          <ul className="mt-2 space-y-1">
            {challenges.slice(0, 3).map((c) => (
              <li key={c.id} className="flex justify-between text-[10px] text-muted-foreground">
                <span className="truncate">{c.prediction}</span>
                <span className="font-bold">{c.status}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Social quiniela */}
      <div className="rounded-xl border border-border/60 bg-background/60 p-3">
        <div className="flex items-center gap-1.5 text-[11px] font-bold"><Users className="h-3 w-3 text-primary" /> Quiniela social</div>
        <input
          value={quinielaName}
          onChange={(e) => setQuinielaName(e.target.value)}
          maxLength={32}
          className="mt-2 w-full rounded-md border border-border/60 bg-background/80 px-2 py-1 text-[11px]"
        />
        <button
          onClick={onCreateQuiniela}
          className="mt-2 w-full rounded-lg bg-primary px-3 py-1.5 text-[11px] font-bold text-primary-foreground"
        >
          Crear y compartir
        </button>
        {quinielas.length > 0 && (
          <ul className="mt-2 space-y-1">
            {quinielas.map((q) => (
              <li key={q.id} className="flex justify-between text-[10px] text-muted-foreground">
                <span className="truncate">{q.name}</span>
                <span className="font-bold">{q.joinedCount} 👥</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Dynamic hashtag */}
      <div className="rounded-xl border border-border/60 bg-background/60 p-3">
        <div className="flex items-center gap-1.5 text-[11px] font-bold"><Hash className="h-3 w-3 text-primary" /> Hashtag del partido</div>
        <div className="mt-1 text-[11px] font-mono break-words">{hashtagBlock}</div>
        <button
          onClick={onCopyHashtags}
          className="mt-2 w-full rounded-lg border border-border/60 bg-background/80 px-3 py-1.5 text-[11px] font-bold"
        >
          Copiar hashtags
        </button>
      </div>

      {/* Auto-referral indicator */}
      <div className="flex items-center gap-1.5 rounded-lg bg-accent/30 px-2 py-1.5 text-[10px] text-muted-foreground">
        <Link2 className="h-3 w-3" />
        <span>Cada link que compartes lleva tu referido automáticamente.</span>
      </div>
    </div>
  );
}
